import { getFocalPointStyles } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import {
    HasProductContext,
    ProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import { ApiParameter, get as ApiGet } from "@insite/client-framework/Services/ApiService";
import { Category } from "@insite/client-framework/Services/CategoryService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { AttributeValueModel, BrandModel, CountryModel, ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductImageCarousel, {
    ProductImageCarouselStyles,
} from "@insite/content-library/Components/ProductImageCarousel";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import LazyImage from "@insite/mobius/LazyImage";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import { get, injectCss } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import ProductDetailPageBreadcrumbs from "@ucs/Components/ProductDetailPageBreadcrumbs";
import ImageSelector from "@ucs/Components/ProductImageViewer";
import { isCategoryLvl1 } from "@ucs/Helpers/CategoryHelpers";
import PageBreadcrumbs, { THEMES } from "@ucs/Overrides/Components/PageBreadcrumbs";
import { getProductBreadcrumbs } from "@ucs/Services/GetProductDetailBreadcrumbService";
import loadCurrentCountires from "@ucs/Store/Custom/LoadCurrentCountries";
import setCurrentCountries from "@ucs/Store/Custom/SetCurrentCountries";
import { getCurrentCountriesState, getIsLoadingCurrentCountriesState } from "@ucs/Store/CustomSelectors";
import { CustomState } from "@ucs/Store/Reducers";
import UCSColors from "@ucs/Utils/UCSColors";
import parse from "html-react-parser";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    bgImage = "bgImage",
    title = "title",
    description = "description",
    focalPoint = "focalPoint",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.bgImage]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
    };
}

const mapStateToProps = (state: CustomState, productContext?: ProductContextModel) => {
    let parentImages;
    if ((productContext as any)?.productContext?.product?.images?.length === 0) {
        parentImages = getProductStateByPath(state, `/Product/${(productContext as any)?.productContext.product.urlSegment}`)
            .value?.images;
    }
    return {
        page: getCurrentPage(state),
        parentImages,
        currentCountries: getCurrentCountriesState(state),
        isLoadingCurrentCountries: getIsLoadingCurrentCountriesState(state),
    };
};

const mapDispatchToProps = {
    loadCurrentCountires,
    setCurrentCountries,
};

const getBody = (
    node: string | React.DetailedReactHTMLElement<{}, HTMLElement> | React.DetailedReactHTMLElement<{}, HTMLElement>[],
) => {
    if (typeof node === 'object') {
        if ("type" in node && node.type === "body") {
            return "children" in node.props ? node.props.children : false;
        } if ("props" in node && "children" in node.props && node.props.children != null && (node.props.children as any)?.length > 0) {
            let returnValue: any = false;

            if (Array.isArray(node.props.children)) {
                node.props.children.forEach((child : React.DetailedReactHTMLElement<{children: any}, HTMLElement> | React.DetailedReactHTMLElement<{}, HTMLElement>[]) => {
                    const bodyContent = getBody(child);
                    if (bodyContent) {
                        returnValue = bodyContent;
                    }
                })
                return returnValue;
            } 
                return node.props.children;
            
        } 
        return false;        
    } 
    return false;
    
};

const enum BannerTypes {
    NORMAL,
    HALFWIDTH,
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasProductContext;

const HeroBannerWrapper = styled.div`
    display: flex;

    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                height: auto;
                flex-direction: column-reverse;
            `,
            css`
                min-height: 50vw;
                flex-direction: column-reverse;
            `,
            css`
                min-height: 45vw;
                flex-direction: column-reverse;
            `,
            css`
                min-height: 30vw;
            `,
            css`
                min-height: 30vw;
            `,
        ])};
`;

const HeroBannerTextSection = styled("div") <{ bannerType: BannerTypes }>`
    background-color: ${UCSColors.gray01};
    color: white;
    width: ${p => (p.bannerType === BannerTypes.HALFWIDTH ? "50%" : "380px")};
    font-size: 20px;
    padding: 3.75rem 30px;

    ${({ theme, bannerType }) =>
        breakpointMediaQueries(theme, [
            css`
                height: 100%;
                padding: 1.25rem 30px;
                width: 100%;
            `,
            css`
                width: ${bannerType === BannerTypes.HALFWIDTH ? "50%" : "100%"};
                padding: 1.25rem 30px;
            `,
            css`
                width: ${bannerType === BannerTypes.HALFWIDTH ? "50%" : "100%"};
                padding: 1.25rem 30px;
            `,
        ])};
    > h1 {
        ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                    margin-top: 0.5rem;
                    margin-bottom: 1rem;
                    font-size: 40px;
                `,
            css`
                    margin-top: 0.5rem;
                    margin-bottom: 1rem;
                    font-size: 40px;
                `,
            css`
                    margin-top: 0.5rem;
                    margin-bottom: 1.5rem;
                `,
            css`
                    margin-top: 0.5rem;
                    margin-bottom: 1.5rem;
                `,
            css`
                    margin-top: 0.5rem;
                    margin-bottom: 1.5rem;
                `,
        ])};
    }

    > p {
        margin-bottom: 0px;
        font-size: 20px;
        line-height: 24px;

        ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                    font-size: 18px;
                `,
            css`
                    font-size: 18px;
                `,
        ])};
    }
`;

const ImageWrapper = styled("div") <{ bgImage: string }>`
    flex: 1;
    background-size: cover;
    background-image: url("${p => p.bgImage}");
    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                height: 40vw;
                flex: none;
                width: 100%;
            `,
        ])};
    ${injectCss}
`;

const ImageSelectorStyles = {
    css: css`
        flex: 1;
        ${({ theme }) =>
            breakpointMediaQueries(theme, [
                css`
                    flex: none;
                    width: 100%;
                    height: 100%;
                    &:before {
                        padding-top: 100%;
                        content: "";
                        display: block;
                    }
                `,
            ])};
    `,
};

const ProductCode = styled.div`
    font-size: 12px;
    margin-bottom: 25px;
`;

const ProductDetailsBanner: React.FC<Props> = props => {
    // Set default values from fields or page
    let title: string = props.fields.title || props.page.fields.title;
    const [description, setDescription] = React.useState<string
    | React.DetailedReactHTMLElement<{}, HTMLElement>
    | React.DetailedReactHTMLElement<{}, HTMLElement>[]
    | undefined>("");
    let bgImage: string = props.fields.bgImage;
    let bannerType: BannerTypes = BannerTypes.NORMAL;
    const [country, setCountry] = React.useState("");
    let useImageSelector = false;
    let footer = "";
    const product: ProductModel = React.useContext(ProductContext).product;
    const [productBreadCrumbs, setProductBreadcrumbs] = React.useState(undefined);

    // Check if Category page and use category data
    const focalPointStyles = getFocalPointStyles(props.fields.focalPoint);
    const imageStyles = {
        css: css`
            ${focalPointStyles}
        `,
    };
    const imageWrapperStyles = {
        css: css`
            position: relative;
            overflow: hidden;
            ${({ theme }) =>
            breakpointMediaQueries(theme, [
                css`
                    width: 100%;
                    flex: 1 1 auto;
                    height: 40vw;
                `,
                css`
                    width: 100%;
                    flex: 1 1 auto;
                    height: 40vw;
                `,
                css`
                    width: 100%;
                    flex: 1 1 auto;
                    height: 50vw;
                `,
                css`
                    width: calc(100% - 380px);
                `,
                css`
                    width: calc(100% - 380px);
                `,
            ])};
            div[class^="LazyImageStyle"] {
                width: 100%;
                img {
                    bottom: -9999px;
                    left: -9999px;
                    margin: auto;
                    right: -9999px;
                    top: -9999px;
                    max-width: 100%;
                    width: 100%;
                    ${({ theme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        css`
                            max-width: 100%;
                            width: 100%;
                            position: absolute;
                            min-height: 100%;
                        `,
                        css`
                            max-width: 100%;
                            width: 100%;
                            position: absolute;
                            min-height: 100%;
                        `,
                    ])};
                }
            }
        `,
    };

    React.useEffect(() => {
        if (productBreadCrumbs === undefined) {
            getProductBreadcrumbs(product.id.toString()).then((result: any) => {
                setProductBreadcrumbs(result);
            });
        }

        if (props.currentCountries.length === 0 && !props.isLoadingCurrentCountries) {
            props.loadCurrentCountires({});
        } else {
            setCurrentCountry(props.currentCountries);
        }
        if(product){
            if (product.content?.htmlContent) {
                const parsedDescription = getBody(parse(product.content?.htmlContent));
                setDescription(parsedDescription || product.content?.htmlContent);
            }
        }
    }, []);

    React.useEffect(() => {
        setCurrentCountry( props.currentCountries);
    },[props.currentCountries]);

    const setCurrentCountry = (countries: CountryModel[]) =>{
        countries.length !== 0 && setCountry(countries[0].abbreviation);
    }

    const populateLocationFooterMessage = (attributeValues: AttributeValueModel[]) => {
        footer = "Stocked in ";
        attributeValues.forEach((attributeValue, i) => {
            if (attributeValues.length > 1 && i === attributeValues.length - 1) {
                footer += " and ";
            }
            footer += attributeValue.value;
            if (i === attributeValues.length - 1) {
                footer += ".";
            } else if (attributeValues.length > 2) {
                footer += ", ";
            }
        });
    };

    if (props.page.type === "CategoryDetailsPage" || props.page.type === "ProductListPage") {
        const category: Category | undefined = React.useContext(CategoryContext);

        if (category) {
            if (props.page.type === "CategoryDetailsPage") {
                bannerType = BannerTypes.HALFWIDTH;
            } else if (props.page.type === "ProductListPage") {
                bannerType = BannerTypes.NORMAL;
            }
            if (!isCategoryLvl1(category.path)) {
                title = category.name;
                setDescription(getBody(parse(category.htmlContent || "")));
                bgImage = category.largeImagePath;
            }
        }
    } else if (props.page.type === "ProductDetailsPage") {
        bannerType = BannerTypes.HALFWIDTH;

        if (product) {
            title = product.productTitle;

            bgImage = product.largeImagePath;

            // Search for locations
            if (product.attributeTypes) {
                if (country) {
                    const locationsCA = product.attributeTypes.find(a => a.name.toLowerCase() === "location_canada");
                    const locationsUS = product.attributeTypes.find(a => a.name.toLocaleLowerCase() === "location_us");

                    if(locationsCA?.attributeValues?.length === 0 && locationsUS?.attributeValues?.length === 0){
                        footer = siteMessage("Location_AlternativeMessage_NA") as string;
                    }else if (country === "CA") {
                        if(locationsCA?.attributeValues && locationsCA.attributeValues.length > 0){
                            populateLocationFooterMessage(locationsCA.attributeValues);
                        }else{
                            footer = siteMessage("Location_AlternativeMessage_CA") as string;
                        }
                    } else if (
                        country !== "CA" 
                    ) {
                        if(locationsUS?.attributeValues && locationsUS.attributeValues.length > 0){
                            populateLocationFooterMessage(locationsUS.attributeValues);
                        }else {
                            footer = siteMessage("Location_AlternativeMessage_US") as string;
                        }
                    } 
                }
            }else{
                footer = siteMessage("Location_AlternativeMessage_NA") as string;
            }

            if (product.images && product.images.length > 0) {
                product.images.forEach((image: ImageModel) => {
                    if (
                        image.name.toLowerCase().includes("_swatch") ||
                        image.name.toLowerCase().includes("_alt1") ||
                        image.name.toLowerCase().includes("_alt2")
                    ) {
                        useImageSelector = true;
                    }
                });
            } else if (props.parentImages) {
                props.parentImages.forEach((image: ImageModel) => {
                    if (
                        image.name.toLowerCase().includes("_swatch") ||
                        image.name.toLowerCase().includes("_alt1") ||
                        image.name.toLowerCase().includes("_alt2")
                    ) {
                        useImageSelector = true;
                    }
                });
            }
        }
    } else if (props.page.type === "BrandsPage") {
        bannerType = BannerTypes.HALFWIDTH;
    } else if (props.page.type === "BrandDetailsPage") {
        bannerType = BannerTypes.HALFWIDTH;
        const brand: BrandModel | undefined = React.useContext(BrandStateContext).value;
        bgImage = brand ? brand.featuredImagePath : "";
        setDescription(brand ? brand.metaDescription : "");
        title = brand ? brand.name : "";
    }

    return (
        <HeroBannerWrapper id="megaMenuContainer">
            <HeroBannerTextSection bannerType={bannerType}>
                {productBreadCrumbs !== undefined && <ProductDetailPageBreadcrumbs productCategory={productBreadCrumbs} theme={THEMES.LIGHT}></ProductDetailPageBreadcrumbs> }
                <Typography variant="h1" as="h1">
                    {title}
                </Typography>
                <Typography variant="p" as="p">
                    {description}
                </Typography>
                {footer && (
                    <Typography variant="p" as="p" size={16} style={{ marginTop: "25px", fontSize: "18px !important" }}>
                        {footer}
                    </Typography>
                )}
            </HeroBannerTextSection>
            {useImageSelector ? (
                <ImageSelector css={ImageSelectorStyles} product={product} />
            ) : (
                <StyledWrapper {...imageWrapperStyles}><LazyImage src={bgImage} {...imageStyles} /></StyledWrapper>
            )}
        </HeroBannerWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Product Details",
    displayName: "Banner",
    allowedContexts: ["ProductDetailsPage"],
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: "Title",
            defaultValue: "",
            editorTemplate: "TextField",
            fieldType: "General",
        },
        {
            name: fields.description,
            displayName: "Description",
            defaultValue: "",
            editorTemplate: "RichTextField",
            fieldType: "General",
        },
        {
            name: fields.bgImage,
            displayName: "Background Image",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            fieldType: "General",
        },
        {
            fieldType: "General",
            name: fields.focalPoint,
            displayName: "Focal Point",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Top Left", value: "topLeft" },
                { displayName: "Top Center", value: "topCenter" },
                { displayName: "Top Right", value: "topRight" },
                { displayName: "Center Left", value: "centerLeft" },
                { displayName: "Center", value: "center" },
                { displayName: "Center Right", value: "centerRight" },
                { displayName: "Bottom Left", value: "bottomLeft" },
                { displayName: "Bottom Center", value: "bottomCenter" },
                { displayName: "Bottom Right", value: "bottomRight" },
            ],
            defaultValue: "center",
            hideEmptyOption: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsBanner)),
    definition,
};

export default widgetModule;
